import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarRangeIcon, LayoutGridIcon } from 'lucide-react';
import { creators } from '../data/creators';
import { CreatorCard } from '../components/creator/CreatorCard';
import { ExhibitionTimeline } from '../components/creator/ExhibitionTimeline';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { getStatus } from '../utils/exhibition';
import { easeOut, staggerDelay } from '../utils/motion';
import type { ExhibitionStatus } from '../types/catalog';
const statusTabs: {
  id: ExhibitionStatus;
  label: string;
}[] = [{
  id: 'current',
  label: 'En vitrine'
}, {
  id: 'upcoming',
  label: 'Bientôt'
}, {
  id: 'past',
  label: 'Déjà passés'
}];
export function Creators() {
  const [params, setParams] = useSearchParams();
  const view = params.get('vue') === 'calendrier' ? 'calendar' : 'grid';
  const statusParam = params.get('statut');
  const status: ExhibitionStatus = statusTabs.some(t => t.id === statusParam) ? statusParam as ExhibitionStatus : 'current';
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const list = creators.filter(c => getStatus(c) === status).sort((a, b) => status === 'past' ? b.exhibitionEnd.localeCompare(a.exhibitionEnd) : status === 'current' ? a.exhibitionEnd.localeCompare(b.exhibitionEnd) : a.exhibitionStart.localeCompare(b.exhibitionStart));
  return <div className="mx-auto max-w-[1440px] px-5 pb-8 pt-8 md:px-8 md:pt-12" data-id="element-805" data-mp="3ihn0o2hd9ad5">
      <Breadcrumbs items={[{
      label: 'Accueil',
      to: '/'
    }, {
      label: 'Créateurs'
    }]} data-id="element-806" data-mp="3dhhev2xs2i2z" />
      <header className="mt-6 grid gap-6 lg:grid-cols-12 lg:items-end" data-id="element-807" data-mp="xq3cl8er7kw7">
        <h1 className="font-display text-5xl leading-[0.98] md:text-7xl lg:col-span-7" data-id="element-808" data-mp="xsftdi8rzp7o">
          Une vingtaine de créateurs, <em className="text-clay" data-id="element-809" data-mp="3mjrqdaethgsx">qui se relaient</em>
        </h1>
        <p className="text-base text-muted md:text-lg lg:col-span-5" data-id="element-810" data-mp="3mlm341fxq2lx">
          Chacun expose environ trois mois, puis laisse sa place. Pendant leur séjour, ils tiennent la boutique à tour de
          rôle : vous les rencontrerez peut-être derrière le comptoir.
        </p>
      </header>

      <div className="mt-12 flex flex-col gap-4 border-b border-line sm:flex-row sm:items-end sm:justify-between" data-id="element-811" data-mp="4d3mfnvi6zou">
        {view === 'grid' ? <ul className="flex gap-7" role="tablist" aria-label="Statut d’exposition" data-id="element-812" data-mp="37u3p296myi57">
            {statusTabs.map(t => {
          const count = creators.filter(c => getStatus(c) === t.id).length;
          const active = status === t.id;
          return <li key={t.id} data-id="element-813" data-mp="2k50z8e1ulizm">
                  <button type="button" role="tab" aria-selected={active} onClick={() => setParam('statut', t.id === 'current' ? null : t.id)} className={`relative whitespace-nowrap pb-3 text-sm transition-colors duration-150 ${active ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-814" data-mp="2k5sufnrj9epd">
                    {t.label} <span className="text-xs" data-id="element-815" data-mp="27ldnnjef94kg">({count})</span>
                    {active && <motion.span layoutId="creator-tab" className="absolute inset-x-0 -bottom-px h-0.5 bg-ink" transition={{
                duration: 0.25,
                ease: easeOut
              }} data-id="element-816" data-mp="23ld11bh65n2b" />}
                  </button>
                </li>;
        })}
          </ul> : <p className="pb-3 text-sm text-muted" data-id="element-817" data-mp="1tsxmko52zgm6">Juin 2026 → février 2027 · {creators.length} créateurs</p>}
        <div className="mb-3 inline-flex self-start rounded-full border border-line p-1 sm:self-auto" role="group" aria-label="Affichage" data-id="element-818" data-mp="1jwuktje55jzr">
          {[{
          id: 'grid',
          label: 'Grille',
          icon: LayoutGridIcon
        }, {
          id: 'calendar',
          label: 'Calendrier',
          icon: CalendarRangeIcon
        }].map(v => {
          const active = view === v.id;
          const Icon = v.icon;
          return <button key={v.id} type="button" aria-pressed={active} onClick={() => setParam('vue', v.id === 'calendar' ? 'calendrier' : null)} className={`relative flex h-9 items-center gap-2 rounded-full px-4 text-sm transition-colors duration-150 ${active ? 'text-paper' : 'text-ink'}`} data-id="element-819" data-mp="3f0iuz8pql4us">
                {active && <motion.span layoutId="view-pill" className="absolute inset-0 rounded-full bg-ink" transition={{
              duration: 0.25,
              ease: easeOut
            }} data-id="element-820" data-mp="2wa15dd07apdn" />}
                <Icon className="relative h-4 w-4" data-id="element-821" data-mp="2ibkztpe2r4x3" />
                <span className="relative" data-id="element-822" data-mp="2n81kuapz935y">{v.label}</span>
              </button>;
        })}
        </div>
      </div>

      <div className="mt-10" data-id="element-823" data-mp="giwoesra1b68">
        <AnimatePresence mode="wait" initial={false} data-id="element-824" data-mp="ggiz70ja2t6l">
          {view === 'calendar' ? <motion.div key="calendar" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-825" data-mp="lfdmaeoa33wi">
              <ExhibitionTimeline creators={creators} data-id="element-826" data-mp="qn3enznmin4v" />
            </motion.div> : <motion.ul key={`grid-${status}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4" data-id="element-827" data-mp="14dyq9jpibic5">
              {list.map((c, i) => <motion.li key={c.id} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.28,
            ease: easeOut,
            delay: staggerDelay(i)
          }} data-id="element-828" data-mp="1nckpet1gqzdf" data-mpi={i}>
                  <CreatorCard creator={c} data-id="element-829" data-mp="3p47mncby7qic" />
                </motion.li>)}
            </motion.ul>}
        </AnimatePresence>
      </div>
    </div>;
}